import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, Button, IconButton, Tooltip } from "@mui/material";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import "./croppingModal.css";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import "../translations.js";

// The cropper UI never sees an image larger than this on its longest edge.
// Big bitmaps inside react-image-crop's overlay are what crashed the tab.
const PREVIEW_MAX_EDGE = 1200;
// Cap final exported image so we don't allocate a 100MP canvas.
const OUTPUT_MAX_EDGE = 1024;

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });

// Downscale `src` to a data URL whose longest edge is <= maxEdge.
// Returns the original src if already small enough.
const downscaleForPreview = async (src, maxEdge) => {
  const img = await loadImage(src);
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  if (longest <= maxEdge) return src;
  const scale = maxEdge / longest;
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/png");
};

const buildInitialPercentCrop = (w, h, aspect = 1) =>
  centerCrop(makeAspectCrop({ unit: "%", width: 90 }, aspect, w, h), w, h);

const CroppingModal = ({ isOpen, src, onCancel, onConfirm, aspect = 1, initialCrop = null }) => {
  const { t } = useTranslation();
  const imgRef = useRef(null);

  const [previewSrc, setPreviewSrc] = useState(null);
  const [crop, setCrop] = useState(undefined);          // % crop, controlled
  const [imageLoaded, setImageLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [prepError, setPrepError] = useState(false);
  // Snapshot the initial crop once per open so prop changes mid-session don't override the user.
  const initialCropSnapshotRef = useRef(null);

  // Reset + (re)prepare preview whenever opened or src changes.
  useEffect(() => {
    if (!isOpen) {
      setPreviewSrc(null);
      setCrop(undefined);
      setImageLoaded(false);
      setBusy(false);
      setPrepError(false);
      initialCropSnapshotRef.current = null;
      return;
    }
    if (!src) return;

    let cancelled = false;
    setPreviewSrc(null);
    setCrop(undefined);
    setImageLoaded(false);
    setPrepError(false);
    initialCropSnapshotRef.current = initialCrop;

    downscaleForPreview(src, PREVIEW_MAX_EDGE)
      .then((preview) => {
        if (!cancelled) setPreviewSrc(preview);
      })
      .catch(() => {
        if (!cancelled) setPrepError(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, src]);

  const onImageLoad = useCallback(
    (e) => {
      const img = e.currentTarget;
      imgRef.current = img;
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) return;
      const seed = initialCropSnapshotRef.current;
      if (
        seed &&
        seed.unit === "%" &&
        Number.isFinite(seed.x) &&
        Number.isFinite(seed.y) &&
        Number.isFinite(seed.width) &&
        Number.isFinite(seed.height) &&
        seed.width > 0 &&
        seed.height > 0
      ) {
        setCrop(seed);
      } else {
        setCrop(buildInitialPercentCrop(w, h, aspect));
      }
      setImageLoaded(true);
    },
    [aspect]
  );

  const handleReset = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    setCrop(buildInitialPercentCrop(img.naturalWidth, img.naturalHeight, aspect));
  }, [aspect]);

  const handleConfirm = useCallback(async () => {
    if (!crop || !crop.width || !crop.height) {
      onCancel?.();
      return;
    }
    setBusy(true);
    try {
      // Crop from the ORIGINAL full-res source; percent is resolution-independent.
      const original = await loadImage(src);
      const nw = original.naturalWidth;
      const nh = original.naturalHeight;

      const sx = Math.max(0, (crop.x / 100) * nw);
      const sy = Math.max(0, (crop.y / 100) * nh);
      let sWidth = (crop.width / 100) * nw;
      let sHeight = (crop.height / 100) * nh;
      sWidth = Math.min(nw - sx, sWidth);
      sHeight = Math.min(nh - sy, sHeight);
      if (sWidth <= 0 || sHeight <= 0) {
        onCancel?.();
        return;
      }

      const scale = Math.min(1, OUTPUT_MAX_EDGE / Math.max(sWidth, sHeight));
      const outW = Math.max(1, Math.round(sWidth * scale));
      const outH = Math.max(1, Math.round(sHeight * scale));

      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(original, sx, sy, sWidth, sHeight, 0, 0, outW, outH);
      onConfirm?.(canvas.toDataURL("image/png"), crop);
    } catch (err) {
      console.error("Crop failed:", err);
      onCancel?.();
    } finally {
      setBusy(false);
    }
  }, [crop, src, onCancel, onConfirm]);

  return (
    <Dialog
      open={isOpen}
      onClose={busy ? undefined : onCancel}
      maxWidth="md"
      fullWidth
      keepMounted={false}
    >
      <DialogContent>
        <div className="crop-header">
          <p className="crop-title">{t("crop_image")}</p>
          <Tooltip title={t("reset")}>
            <span>
              <IconButton size="small" onClick={handleReset} disabled={!imageLoaded || busy}>
                <FontAwesomeIcon icon={faArrowsRotate} />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <div className="crop-canvas-wrapper">
          {prepError && (
            <p style={{ color: "#b71c1c", margin: 0 }}>
              {t("crop_image_load_failed")}
            </p>
          )}
          {!prepError && !previewSrc && (
            <p style={{ color: "#666", margin: 0 }}>
              {t("loading")}
            </p>
          )}
          {previewSrc && (
            <ReactCrop
              crop={crop}
              onChange={(_pixel, percent) => setCrop(percent)}
              aspect={aspect}
              keepSelection
              ruleOfThirds
              minWidth={20}
              minHeight={20}
            >
              <img
                ref={imgRef}
                src={previewSrc}
                alt={t("crop_target_alt")}
                onLoad={onImageLoad}
                draggable={false}
                style={{
                  display: "block",
                  margin: "0 auto",
                  maxWidth: "100%",
                  maxHeight: "60vh",
                  width: "auto",
                  height: "auto",
                  userSelect: "none",
                }}
              />
            </ReactCrop>
          )}
        </div>

        <div className="crop-button-container">
          <Button
            onClick={onCancel}
            variant="outlined"
            disabled={busy}
            startIcon={<FontAwesomeIcon icon={faRotateLeft} />}
            sx={{
              color: "#5616ea",
              borderColor: "#5616ea",
              "&:hover": {
                borderColor: "#5e0fb3",
                backgroundColor: "#f3eaff",
              },
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!imageLoaded || !crop || busy}
            sx={{
              backgroundColor: "#5616ea",
              "&:hover": {
                backgroundColor: "#4a14d6",
              },
            }}
          >
            {t("confirm")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CroppingModal;
